/**
 * parchmentCard.js — shared constants + StageKicker component for the
 * journey pages (Bodha / Sankalpa / Sadhana / Siddhi).
 *
 * SINGLE SOURCE OF TRUTH — do not redefine these values anywhere else.
 * They must stay pixel-identical to the homepage "YOUR JOURNEY" card
 * and to the Bodha ArtefactCards.
 *
 * StageKicker renders the small gold identity crest that sits at the top
 * of every journey stage page: [icon] + KICKER · देवनागरी. It ties each
 * page visually back to its matching homepage arch.
 */

import React from "react";

// ── Parchment card aesthetic (pixel-identical to homepage YOUR JOURNEY) ──
export const CARD_BG           = "rgba(255, 248, 224, 0.88)";
export const CARD_BORDER       = "1px solid rgba(201, 168, 76, 0.35)";
export const CARD_RADIUS       = 12;
export const CARD_SHADOW       = "0 6px 18px rgba(44, 24, 16, 0.16)";
export const CARD_SHADOW_HOVER = "0 10px 24px rgba(44, 24, 16, 0.22)";

// ── Gold token for kickers / accents (same as homepage arch Devanagari) ──
export const GOLD_ACCENT = "#C9A84C";

// ── Stage icons (reuse the exact PNGs from the homepage How-It-Works arches) ──
export const STAGE_ICONS = {
  bodha:    "/homepage-assets/asset-5-icon-book-1.png",
  sankalpa: "/homepage-assets/asset-5-icon-flame-1.png",
  sadhana:  "/homepage-assets/asset-5-icon-lotus-1.png",
  siddhi:   "/homepage-assets/asset-5-icon-tree-1.png",
};

// ── Stage flank illustrations (watercolour vertical strips, left flank only) ──
export const STAGE_FLANK_IMAGES = {
  bodha:    "/illustrations/stage-bodha.png",
  sankalpa: "/illustrations/stage-sankalpa.png",
  sadhana:  "/illustrations/stage-sadhana.png",
  siddhi:   "/illustrations/stage-siddhi.png",
};

/**
 * StageLeftIllustration — decorative watercolour illustration placed
 * BELOW the CurrentBookPanel in the left grid column of /bodha and
 * /sankalpa.
 *
 * Cropping contract (2026-02 founder review):
 *  - TOP of image is anchored to the top of the container.
 *  - Container `overflow: hidden` — if the image is taller than the
 *    available vertical space, the BOTTOM crops out. The archway of
 *    light (Bodha) and glowing gate (Sankalpa) — the visual centrepiece
 *    of each image — always render intact at the top.
 *  - Container uses `flex: 1 1 auto` inside the `bodha-left-col` /
 *    `sankalpa-left-col` flex wrapper, so it naturally fills whatever
 *    space is available below the sticky book panel. Wide viewports
 *    with taller shells reveal more of the image; shorter shells crop
 *    the bottom (plaque area) cleanly.
 *  - `aria-hidden` + `pointer-events: none` — purely decorative.
 *  - Hidden below 900px via `.stage-left-illustration { display: none }`.
 */
export function StageLeftIllustration({ stage }) {
  const src = STAGE_FLANK_IMAGES[stage];
  if (!src) return null;
  return (
    <div
      className="stage-left-illustration"
      aria-hidden="true"
      style={{
        marginTop: 24,
        width: "100%",
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: 720,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <img
        src={src}
        alt=""
        className="stage-flank-feather"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
        }}
      />
    </div>
  );
}

/**
 * StageBottomBanner — horizontal decorative banner placed below the
 * content shell on /sadhana (and potentially other short-shell pages).
 * Horizontal crop of the same PNG used elsewhere; centered vertically
 * within its container so it feels intentional whether the below-shell
 * gap is large (error state, ~411px) or smaller (populated state).
 *
 *  - Cap height at ~170px (wide-and-short banner shape per space audit).
 *  - `object-fit: cover; object-position: center 35%` — pulls the crop
 *    slightly above center to keep the waterfall + path visible rather
 *    than pure center (which would sit under the mid-section).
 *  - Bordered + shadowed the same as parchment cards for visual parity.
 *  - `aria-hidden` + `pointer-events: none` — purely decorative.
 *  - Hidden below 900px via `.stage-bottom-banner { display: none }`.
 */
export function StageBottomBanner({ stage }) {
  const src = STAGE_FLANK_IMAGES[stage];
  if (!src) return null;
  return (
    <div
      className="stage-bottom-banner"
      aria-hidden="true"
      style={{
        maxWidth: 1096,       // shell 1160 minus 32*2 padding on Sadhana
        margin: "48px auto",
        height: 170,
        overflow: "hidden",
        borderRadius: CARD_RADIUS,
        border: CARD_BORDER,
        boxShadow: CARD_SHADOW,
        pointerEvents: "none",
      }}
    >
      <img
        src={src}
        alt=""
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center 35%",
        }}
      />
    </div>
  );
}

/**
 * StageEmptyStateFlank — decorative watercolour illustration placed in the
 * LEFT gutter (~410px wide symmetric side space) of the EMPTY STATE branch
 * of /bodha, /sankalpa, /sadhana. Renders BEFORE any book is selected —
 * intentionally NOT gated behind book context (unlike StageLeftIllustration /
 * StageBottomBanner which live in the populated state).
 *
 * Positioning contract (2026-02 founder review):
 *  - Wrapper is `position: absolute; top:0; left:0; bottom:0; width:410px`
 *    — stretches to the full height of the parent (an empty-state wrapper
 *    with `position: relative`) so it never overlaps the CTA / footer.
 *  - Bodha & Sankalpa (portrait source): `width:100%; height:auto`
 *    top-anchored — the archway of light / glowing gate stays intact at
 *    top, bottom crops via `overflow: hidden` (same logic as
 *    StageLeftIllustration).
 *  - Sadhana (horizontal source, waterfall on the right side of image):
 *    `object-fit: cover` with `objectPosition: 75% center` to isolate the
 *    waterfall column into a portrait crop — reuses the same PNG without
 *    needing new art.
 *  - `aria-hidden` + `pointer-events: none` — purely decorative, never
 *    intercepts clicks on the centred content or CTA.
 *  - Hidden below 900px via `.stage-empty-flank { display: none }` set on
 *    each page's responsive stylesheet.
 */
/**
 * StageEmptyStateFlank — decorative watercolour illustration placed in the
 * LEFT gutter (~410px wide symmetric side space) of the EMPTY STATE branch
 * of /bodha, /sankalpa, /sadhana. Renders BEFORE any book is selected —
 * intentionally NOT gated behind book context (unlike StageLeftIllustration /
 * StageBottomBanner which live in the populated state).
 *
 * Positioning contract (2026-02 founder review):
 *  - Wrapper is `position: absolute; top:0; left:0; bottom:0; width:410px`
 *    — stretches to the full height of the parent (an empty-state wrapper
 *    with `position: relative; minHeight: {natural image height @410 wide}`)
 *    so the ENTIRE portrait renders, including its bottom plaque, and
 *    the surrounding <main> naturally grows to accommodate it.
 *  - Image is always rendered at `width: 100%; height: auto` (natural
 *    aspect at 410px wide) for all three stages — no zoom, no cropping.
 *    Bodha ~1025px tall @ 410 wide, Sankalpa ~864px, Sadhana ~1025px.
 *  - Caller sets the wrapper's minHeight to the matching per-stage number
 *    so the flank stretches to fit the whole image cleanly.
 *  - `aria-hidden` + `pointer-events: none` — purely decorative, never
 *    intercepts clicks on the centred content or CTA.
 *  - Hidden below 900px via `.stage-empty-flank { display: none }` set on
 *    each page's responsive stylesheet.
 */
export function StageEmptyStateFlank({ stage }) {
  const src = STAGE_FLANK_IMAGES[stage];
  if (!src) return null;
  return (
    <div
      className="stage-empty-flank"
      aria-hidden="true"
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        bottom: 0,
        width: 410,
        overflow: "hidden",
        pointerEvents: "none",
      }}
    >
      <img
        src={src}
        alt=""
        className="stage-flank-feather"
        style={{
          display: "block",
          width: "100%",
          height: "auto",
        }}
      />
    </div>
  );
}

/**
 * StageKicker — gold identity crest for the top of a journey stage page.
 *
 * Props:
 *   stage    — 'bodha' | 'sankalpa' | 'sadhana' | 'siddhi'  (icon lookup)
 *   text     — "BODHA · बोध"  (or similar; caller controls the string)
 *   size     — 'default' | 'large'  (large = empty-state variant)
 *   testId   — optional test id for the whole crest
 */
export function StageKicker({ stage, text, size = "default", testId }) {
  const isLarge = size === "large";
  const iconSize = isLarge ? 44 : 36;
  const fontSize = isLarge ? 14 : 13;
  const gap      = isLarge ? 10 : 8;
  const iconSrc  = STAGE_ICONS[stage];

  return (
    <div
      data-testid={testId}
      className={`stage-kicker stage-kicker--${size}`}
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap,
        marginBottom: isLarge ? 14 : 10,
      }}
    >
      {iconSrc && (
        <img
          src={iconSrc}
          alt=""
          aria-hidden="true"
          className="stage-kicker__icon"
          style={{
            height: iconSize,
            width: "auto",
            display: "block",
          }}
        />
      )}
      <p
        className="stage-kicker__text m-0"
        style={{
          fontFamily: "'Cinzel', 'Playfair Display', serif",
          fontSize,
          fontWeight: 700,
          letterSpacing: "0.22em",
          color: GOLD_ACCENT,
          textShadow: "0 1px 0 rgba(255,245,210,0.5)",
        }}
      >
        {text}
      </p>
    </div>
  );
}
